import { useState } from 'react';
import {
  type ColumnDef,
  type Row,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useTableFiltersAndSorting } from '~/hooks/tables/useTableFiltersAndSorting';

type UseDataTableProps<TData, TValue> = {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  enableRowSelection?: (row: Row<TData>) => boolean;
  enablePagination?: boolean;
  enableQueryParam?: boolean;
  enableNavigation?: boolean;
  // enable local storage if key provided
  storageKey?: string;
  // prefix for query params to avoid conflicts when multiple tables are on the same page
  queryParamPrefix?: string;
  pinnedColumns?: {
    left?: string[];
    right?: string[];
  };
  // For server-side pagination: total row count from API
  // When provided, enables manual pagination mode
  rowCount?: number;
  // When true, disables client-side filtering (for server-side search)
  manualFiltering?: boolean;
};

export function useDataTable<TData, TValue>({
  columns,
  data,
  enablePagination = false,
  enableQueryParam = false,
  enableNavigation = true,
  storageKey,
  queryParamPrefix,
  enableRowSelection,
  pinnedColumns,
  rowCount,
  manualFiltering,
}: UseDataTableProps<TData, TValue>) {
  const [rowSelection, setRowSelection] = useState({});

  const {
    globalFilter,
    columnFilters,
    columnVisibility,
    sorting,
    pagination,
    handleGlobalFilterChange,
    handleColumnFiltersChange,
    handleColumnVisibilityChange,
    handleSortingChange,
    handlePaginationChange,
  } = useTableFiltersAndSorting({
    columns,
    enableQueryParam,
    storageKey,
    queryParamPrefix,
  });

  const table = useReactTable({
    data,
    columns,
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleColumnFiltersChange,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    // https://github.com/TanStack/table/issues/4614#issuecomment-2014158617
    // Otherwise we get a table infinite re-render
    // These 'autoReset' attributes can not be conditionally set because otherwise the pagination resets on tab switch
    autoResetPageIndex: false,
    autoResetExpanded: false,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: manualFiltering ? undefined : getFilteredRowModel(),
    manualFiltering,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: handlePaginationChange,
    onGlobalFilterChange: handleGlobalFilterChange,

    enableRowSelection,
    // Server-side pagination: when rowCount is provided, use manual pagination
    manualPagination: rowCount !== undefined,
    rowCount,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination,
      globalFilter,
    },
    initialState: {
      columnPinning: {
        left: pinnedColumns?.left,
        right: [...(pinnedColumns?.right ?? []), 'actions'],
      },
    },
    meta: {
      enableNavigation,
    },
  });

  return table;
}
